import React, { useRef, useEffect } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import utilsScript from '../../util/intlTelInputUtils';
import css from './FieldPhoneInput.css';
import { Field } from 'react-final-form';
import { ValidationError } from '..';

const DEFAULT_OPTIONS = {
  separateDialCode: true,
  utilsScript: utilsScript,
  nationalMode: true,
  numberType: 'FIXED_LINE',
  preferredCountries: ['au'],
};

const FieldPhoneInputComponent = props => {
  const {
    rootClassName,
    className,
    label,
    input,
    options = DEFAULT_OPTIONS,
    meta,
    customOnBlur = () => { },
  } = props;

  const { onFocus, onBlur, value, onChange, ...rest } = input;

  const phoneInputRef = useRef(null);
  const instance = useRef(null);

  useEffect(() => {
    if (typeof window !== undefined) {
      instance.current = window.intlTelInput(phoneInputRef.current, options);
    }

    return () => {
      if (instance.current && instance.current.destroy) instance.current.destroy();
    };
  }, []);

  useEffect(() => {
    if (typeof value === 'string' && instance.current) {
      instance.current.setNumber(value);
    } else if (typeof value === 'object') {
      const { phoneNumber } = value || {};
      if (phoneNumber && instance.current) {
        instance.current.setNumber(phoneNumber);
      }
    }
  }, [JSON.stringify(value)]);

  const handleBlur = () => {
    if (phoneInputRef.current.value.trim()) {
      const selectedCountry = instance.current.getSelectedCountryData();
      const { dialCode, iso2 } = selectedCountry;
      onChange({
        ...value,
        dialCode,
        iso2,
        phoneNumber: instance.current.getNumber(),
      });
      customOnBlur({
        ...value,
        dialCode,
        iso2,
        phoneNumber: instance.current.getNumber(),
      });
    } else {
      onChange(null);
      customOnBlur(null);
    }
    onBlur();
  };

  const handleCountryChange = () => {
    if (instance.current) {
      const selectedCountry = instance.current.getSelectedCountryData();
      const { dialCode, iso2 } = selectedCountry;
      if (typeof value === 'string') {
        onChange({ phoneNumber: value, dialCode, iso2 });
      } else {
        onChange({ ...value, dialCode, iso2 });
      }
    }
  };

  useEffect(() => {
    if (phoneInputRef.current) {
      phoneInputRef.current.addEventListener('countrychange', handleCountryChange);
    }

    return () => {
      if (phoneInputRef.current) {
        phoneInputRef.current.removeEventListener('countrychange', handleCountryChange);
      }
    };
  }, []);

  const classes = classNames(rootClassName || css.root, className);
  const { valid, invalid, touched, error } = meta;
  const hasError = !!(touched && invalid && error);
  const inputClasses = classNames(css.input, {
    [css.valid]: valid,
    [css.error]: hasError,
  });

  useEffect(() => {
    if (typeof window !== undefined) {
      const flagContainer = document.querySelector('.iti__flag-container');
      if (valid) {
        flagContainer.classList.add(css.valid);
        flagContainer.classList.remove(css.error);
      } else {
        if (hasError) {
          flagContainer.classList.remove(css.valid);
          flagContainer.classList.add(css.error);
        }
      }
    }
  }, [valid]);

  return (
    <div className={classes}>
      {label ? <label htmlFor={'phone'}>{label}</label> : null}
      <input
        type="tel"
        {...rest}
        ref={phoneInputRef}
        className={inputClasses}
        onBlur={handleBlur}
        onFocus={onFocus}
      />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

FieldPhoneInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
};

FieldPhoneInputComponent.propTypes = {
  rootClassName: string,
  className: string,
};

const FieldPhoneInput = props => <Field component={FieldPhoneInputComponent} {...props} />;

export default FieldPhoneInput;
